import React, { FunctionComponent, useState, useEffect } from 'react';
import { Flex } from '@rebass/emotion';

import {
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants as MagentoProductVariant,
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_configurableOptions as ConfigurableOptions,
  PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants_attributes as Variant,
} from '../../hook/__generated__/PricingPageShopProduct';
import Dropdown from '../Dropdown';
import { attributeCodes, getDropdownData, getVariantOptions } from './helpers';

const ProductVariant: FunctionComponent<{
  variants: Array<MagentoProductVariant | null>;
  selectedVariant?: MagentoProductVariant | null;
  configurableOptions: Array<ConfigurableOptions | null>;
  onChange: (value: MagentoProductVariant) => void;
}> = ({ variants, configurableOptions, onChange, selectedVariant }) => {
  const [selectedOptions, setSelectedOptions] = useState<Variant | {}>({});
  const [variantOptions, setVariantOptions] = useState<
    Array<ConfigurableOptions | null>
  >([]);

  useEffect(() => {
    if (selectedVariant) {
      setSelectedOptions(getVariantOptions(selectedVariant) || {});
      setVariantOptions(
        getDropdownData(configurableOptions, selectedVariant, variants)
      );
    }
  }, [selectedVariant]);

  const findAlternativeVariant = ({ prevState, newVariants }) => {
    const secondaryOptions = configurableOptions?.find(
      (options) => options?.attributeCode === attributeCodes.secondary
    );

    onDropDownChange({
      key: secondaryOptions?.attributeCode,
      value: secondaryOptions?.values?.find((value) => {
        return (
          secondaryOptions?.attributeCode &&
          value?.uid !== prevState[secondaryOptions?.attributeCode]
        );
      })?.uid,
      prevState: newVariants,
    });
  };

  const onDropDownChange = ({ key, value, prevState }) => {
    const newVariants = {
      ...prevState,
      [key]: value,
    };

    const matchingVariant = variants.find((variant) => {
      const matchingAttributes = variant?.attributes?.filter(
        (attr) => attr?.code && attr?.uid === newVariants[attr?.code]
      );

      return matchingAttributes?.length === variant?.attributes?.length;
    });

    if (matchingVariant) {
      onChange(matchingVariant);
    } else {
      findAlternativeVariant({ prevState, newVariants });
    }
  };

  return (
    <Flex flexDirection="column">
      {variantOptions.map((item, index) => {
        if (!item || !item.values) {
          return null;
        }

        const options = item.values
          .filter((option) => option?.label && option?.uid)
          .map((option) => ({
            label: option?.label || '',
            value: option?.uid || '',
            key: option?.uid || '',
          }));

        return (
          <Flex flexDirection="column" mt={index > 0 ? 10 : 0} key={item.uid}>
            <Dropdown
              options={options}
              value={selectedOptions[item.attributeCode || '']}
              onChange={(value) =>
                onDropDownChange({
                  key: item.attributeCode,
                  value,
                  prevState: selectedOptions,
                })
              }
              disabled={item.values?.length === 1}
              data-testid={`${item.attributeCode}-dropdown`}
            />
          </Flex>
        );
      })}
    </Flex>
  );
};

export default ProductVariant;
